import React, {useState, useRef, useMemo, useCallback, Fragment, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import debounce from 'lodash.debounce';
import moment from 'moment';
import {withRouter} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {SearchAutocomplete} from '@ace-de/eua-google-maps-client';
import {useStyles, Panel, AutosuggestField, Option, DataRow, Divider} from '@ace-de/ui-components';
import {ButtonPrimary, Input, Checkbox, Form, TimeField, InputCurrency} from '@ace-de/ui-components/form';
import {Icon, locationIcon, checkmarkIcon} from '@ace-de/ui-components/icons';
import * as serviceAssignmentActionTypes from '../../service-assignments/serviceAssignmentActionTypes';
import config from '../../config';
import * as serviceCaseActionTypes from '../../service-cases/serviceCaseActionTypes';
import parseGoogleAddress from '../../utils/parseGoogleAddress';

const toFormData = finalDestination => {
    if (!finalDestination) return null;
    return {
        locationName: finalDestination.location?.locationName || '',
        phoneNo: finalDestination.phoneNo || '',
        workingHoursFrom: finalDestination.workingHoursFrom || '',
        workingHoursTo: finalDestination.workingHoursTo || '',
        alternativeWorkingHoursFrom: finalDestination.alternativeWorkingHoursFrom || '',
        alternativeWorkingHoursTo: finalDestination.alternativeWorkingHoursTo || '',
        dailyFee: finalDestination.dailyFee || '',
    };
};

const EMPTY_FORM_DATA = {
    locationName: '',
    phoneNo: '',
    workingHoursFrom: '',
    workingHoursTo: '',
    alternativeWorkingHoursFrom: '',
    alternativeWorkingHoursTo: '',
    dailyFee: '',
};

const EMPTY_ERRORS = {
    location: '',
    workingHoursFrom: '',
    workingHoursTo: '',
    alternativeWorkingHoursFrom: '',
    alternativeWorkingHoursTo: '',
};

const SCBasicDataTowingDestinationInfoPanel = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, translate} = useTranslate();
    const translateTab = createTranslateShorthand('sav_commissioning_tab');
    const {latestServiceAssignmentWithTowingDestination, serviceCase, serviceAssignments} = props;
    const {searchAssignmentFinalDestinationLocationGeolocation, initiateUpdateServiceCaseFinalDestination} = props;
    const firstServiceAssignmentId = serviceCase?.serviceAssignmentIds?.[0];
    const firstServiceAssignment = latestServiceAssignmentWithTowingDestination
        || serviceAssignments?.[firstServiceAssignmentId];

    const [formData, setFormData] = useState(
        toFormData(serviceCase?.finalDestination) || EMPTY_FORM_DATA,
    );
    const [selectedLocation, setSelectedLocation] = useState(
        serviceCase?.finalDestination?.location || null,
    );
    const [isIdenticalToTowingDestination, setIsIdenticalToTowingDestination] = useState(
        !!serviceCase?.finalDestination?.location?.id
        && !!latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.id
        && serviceCase.finalDestination.location.id === latestServiceAssignmentWithTowingDestination.towingDestination.location.id, // eslint-disable-line max-len
    );
    const [errors, setErrors] = useState(EMPTY_ERRORS);
    const [locationSearchQuery, setLocationSearchQuery] = useState('');

    const lastLocationSearchQuery = useRef('');
    const lastSyncedUpdatedAt = useRef(serviceCase?.finalDestination?.updatedAt ?? null);

    // Sync local state when Redux serviceCase changes
    useEffect(() => {
        const incomingUpdatedAt = serviceCase?.finalDestination?.updatedAt ?? null;
        if (incomingUpdatedAt === lastSyncedUpdatedAt.current) return;
        lastSyncedUpdatedAt.current = incomingUpdatedAt;

        setFormData(toFormData(serviceCase?.finalDestination) || EMPTY_FORM_DATA);
        setSelectedLocation(serviceCase?.finalDestination?.location || null);
        setIsIdenticalToTowingDestination(
            !!serviceCase?.finalDestination?.location?.id
            && !!latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.id
            && serviceCase.finalDestination.location.id === latestServiceAssignmentWithTowingDestination?.towingDestination.location.id, // eslint-disable-line max-len
        );
    }, [serviceCase, latestServiceAssignmentWithTowingDestination]);

    const searchFinalDestinationLocationGeolocationDebounced = useMemo(() => {
        if (typeof searchAssignmentFinalDestinationLocationGeolocation === 'function') {
            return debounce(
                searchAssignmentFinalDestinationLocationGeolocation,
                config.ARCGIS_ADDRESS_SUGGEST_GEOLOCATION_DEBOUNCE_TIMER,
            );
        }
        return () => null;
    }, [searchAssignmentFinalDestinationLocationGeolocation]);

    const handleFieldChange = useCallback((field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
    }, []);

    const handleLocationSearchQueryChange = useCallback(searchQueryString => {
        setLocationSearchQuery(searchQueryString);
        if (!searchQueryString) setSelectedLocation(null);

        if (searchQueryString
            && searchQueryString.toLowerCase() !== (selectedLocation?.formattedAddress || '').toLowerCase()
            && searchQueryString.length >= config.MINIMUM_SEARCH_QUERY_LENGTH) {
            searchFinalDestinationLocationGeolocationDebounced({
                searchQueryString,
                serviceAssignmentId: latestServiceAssignmentWithTowingDestination
                    ? `${latestServiceAssignmentWithTowingDestination?.serviceCaseId}-${latestServiceAssignmentWithTowingDestination?.lineNo}`
                    : serviceCase.serviceAssignmentIds?.[0],
            });
            lastLocationSearchQuery.current = searchQueryString;
        }
    }, [
        selectedLocation,
        searchFinalDestinationLocationGeolocationDebounced,
        latestServiceAssignmentWithTowingDestination,
        serviceCase,
    ]);

    const handleLocationCandidateSelect = useCallback(async locationCandidate => {
        const parsedAddress = !config.IS_FALLBACK_SYSTEM
            ? locationCandidate
            : await parseGoogleAddress(locationCandidate, translate);

        setSelectedLocation(parsedAddress || null);
        setFormData(prev => ({
            ...prev,
            locationName: parsedAddress?.locationName || prev.locationName,
        }));
    }, [translate]);

    // false→true: copy towing destination location into form
    // true→false: clear form
    const handleOnCheckboxChange = useCallback(value => {
        if (value) {
            const towingLocation = latestServiceAssignmentWithTowingDestination?.towingDestination?.location || null;
            setSelectedLocation(towingLocation);
            setFormData({
                ...EMPTY_FORM_DATA,
                locationName: towingLocation?.locationName || '',
                phoneNo: towingLocation?.phoneNo || '',
            });
        } else {
            setSelectedLocation(null);
            setFormData(EMPTY_FORM_DATA);
            setLocationSearchQuery('');
            lastLocationSearchQuery.current = '';
        }
        setIsIdenticalToTowingDestination(value);
    }, [latestServiceAssignmentWithTowingDestination]);

    const handleValidation = useCallback(() => {
        let hasError = false;
        const updatedErrors = {...EMPTY_ERRORS};
        const timeRegex = /^(([0-1][0-9])|([2][0-3])):[0-5][0-9]$/;

        if (!selectedLocation?.id) {
            updatedErrors.location = translateTab('error_message.empty_address');
            hasError = true;
        }

        if (formData.workingHoursFrom && !timeRegex.test(formData.workingHoursFrom)) {
            updatedErrors.workingHoursFrom = translateTab('error_message.invalid_time_format');
            hasError = true;
        }

        if (formData.workingHoursTo && !timeRegex.test(formData.workingHoursTo)) {
            updatedErrors.workingHoursTo = translateTab('error_message.invalid_time_format');
            hasError = true;
        }

        if ((!formData.workingHoursFrom && formData.workingHoursTo)
            || (!formData.workingHoursTo && formData.workingHoursFrom)) {
            updatedErrors.workingHoursFrom = translateTab('error_message.invalid_time_range');
            updatedErrors.workingHoursTo = translateTab('error_message.invalid_time_range');
            hasError = true;
        }

        if (formData.workingHoursFrom && formData.workingHoursTo
            && formData.workingHoursFrom >= formData.workingHoursTo) {
            updatedErrors.workingHoursFrom = translateTab('error_message.invalid_time_range');
            updatedErrors.workingHoursTo = translateTab('error_message.invalid_time_range');
            hasError = true;
        }

        if (formData.alternativeWorkingHoursFrom && !timeRegex.test(formData.alternativeWorkingHoursFrom)) {
            updatedErrors.alternativeWorkingHoursFrom = translateTab('error_message.invalid_time_format');
            hasError = true;
        }

        if (formData.alternativeWorkingHoursTo && !timeRegex.test(formData.alternativeWorkingHoursTo)) {
            updatedErrors.alternativeWorkingHoursTo = translateTab('error_message.invalid_time_format');
            hasError = true;
        }

        if ((!formData.alternativeWorkingHoursFrom && formData.alternativeWorkingHoursTo)
            || (!formData.alternativeWorkingHoursTo && formData.alternativeWorkingHoursFrom)) {
            updatedErrors.alternativeWorkingHoursFrom = translateTab('error_message.invalid_time_range');
            updatedErrors.alternativeWorkingHoursTo = translateTab('error_message.invalid_time_range');
            hasError = true;
        }

        if (formData.alternativeWorkingHoursFrom && formData.alternativeWorkingHoursTo
            && formData.alternativeWorkingHoursFrom >= formData.alternativeWorkingHoursTo) {
            updatedErrors.alternativeWorkingHoursFrom = translateTab('error_message.invalid_time_range');
            updatedErrors.alternativeWorkingHoursTo = translateTab('error_message.invalid_time_range');
            hasError = true;
        }

        return {hasError, updatedErrors};
    }, [formData, selectedLocation, translateTab]);

    const handleOnSubmit = useCallback(() => {
        const {hasError, updatedErrors} = handleValidation();
        setErrors(updatedErrors);
        if (hasError) return;

        initiateUpdateServiceCaseFinalDestination({
            serviceCaseId: serviceCase.id,
            caseType: serviceCase.caseType,
            serviceCaseData: {
                finalDestination: {
                    location: {
                        ...selectedLocation,
                        locationName: formData.locationName,
                    },
                    phoneNo: formData.phoneNo,
                    workingHoursFrom: formData.workingHoursFrom,
                    workingHoursTo: formData.workingHoursTo,
                    alternativeWorkingHoursFrom: formData.alternativeWorkingHoursFrom,
                    alternativeWorkingHoursTo: formData.alternativeWorkingHoursTo,
                    dailyFee: formData.dailyFee,
                },
            },
        });
    }, [handleValidation, formData, selectedLocation, serviceCase, initiateUpdateServiceCaseFinalDestination]);

    const isFinalDestinationSaved = !!serviceCase?.finalDestination?.location?.id
        && !!selectedLocation?.id
        && serviceCase?.finalDestination.location?.id === selectedLocation.id;
    const isFormTouched = serviceCase?.finalDestination?.location?.locationName !== formData.locationName
        || serviceCase?.finalDestination?.phoneNo !== formData.phoneNo
        || serviceCase?.finalDestination?.workingHoursFrom !== formData.workingHoursFrom
        || serviceCase?.finalDestination?.workingHoursTo !== formData.workingHoursTo
        || serviceCase?.finalDestination?.alternativeWorkingHoursFrom !== formData.alternativeWorkingHoursFrom
        || serviceCase?.finalDestination?.alternativeWorkingHoursTo !== formData.alternativeWorkingHoursTo
        || `${serviceCase?.finalDestination?.dailyFee}` !== `${formData.dailyFee}`;

    return (
        <Panel
            qaIdent="last-assigned-towing-destination"
            contentClassName={cx('global!ace-u-two-column-layout')}
        >
            <div>
                <h3
                    className={cx([
                        'global!ace-u-margin--top-16',
                        'global!ace-u-typography--color-default',
                        'global!ace-u-typography--variant-h3',
                    ])}
                >
                    {translateTab('panel_title.last_assigned_towing_destination')}
                </h3>
                <p
                    className={cx([
                        'global!ace-u-typography--variant-caption',
                        'global!ace-u-margin--top-8',
                        'global!ace-u-margin--bottom-24',
                    ])}
                >
                    {translateTab('time_label.last_update', {
                        assignedAt: latestServiceAssignmentWithTowingDestination?.towingDestination?.updatedAt
                            ? moment(
                                latestServiceAssignmentWithTowingDestination.towingDestination.updatedAt,
                            ).format('DD.MM.YYYY - HH:mm')
                            : '-',
                    })}
                </p>
                <DataRow
                    label={translateTab('data_row_label.name')}
                    className={cx('ace-c-data-row--condensed')}
                    qaIdent="location-name"
                >
                    {latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.locationName || '-'}
                </DataRow>
                <DataRow
                    label={translateTab('data_row_label.address')}
                    className={cx('ace-c-data-row--condensed')}
                    qaIdent="address"
                >
                    {latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.formattedAddress || [
                        latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.street,
                        latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.postCode,
                        latestServiceAssignmentWithTowingDestination?.towingDestination?.location?.city,
                    ].filter(value => !!value).join(', ') || '-'}
                </DataRow>
                <Checkbox
                    name="isIdenticalToTowingDestination"
                    value={true}
                    isSelected={isIdenticalToTowingDestination}
                    onChange={handleOnCheckboxChange}
                    className={cx('global!ace-u-margin--top-128')}
                    isDisabled={!latestServiceAssignmentWithTowingDestination?.towingDestination
                        || ![
                            latestServiceAssignmentWithTowingDestination.towingDestination.location?.locationName,
                            latestServiceAssignmentWithTowingDestination.towingDestination.location?.street,
                            latestServiceAssignmentWithTowingDestination.towingDestination.location?.postCode,
                            latestServiceAssignmentWithTowingDestination.towingDestination.location?.city,
                        ].filter(value => !!value).length}
                >
                    {translateTab('checkbox_label.apply_as_a_final_destination')}
                </Checkbox>
            </div>
            <div>
                <h3
                    className={cx([
                        'global!ace-u-margin--top-16',
                        'global!ace-u-typography--color-default',
                        'global!ace-u-typography--variant-h3',
                    ])}
                >
                    {translateTab('headline_text.final_towing_destination')}
                </h3>
                <p
                    className={cx([
                        'global!ace-u-typography--variant-caption',
                        'global!ace-u-margin--top-8',
                        'global!ace-u-margin--bottom-24',
                    ])}
                >
                    {translateTab('time_label.last_update', {
                        assignedAt: serviceCase?.finalDestination?.updatedAt
                            ? moment(
                                serviceCase.finalDestination.updatedAt,
                            ).format('DD.MM.YYYY - HH:mm')
                            : '-',
                    })}
                </p>
                <Form name="finalDestinationForm" onSubmit={handleOnSubmit}>
                    <DataRow
                        label={translateTab('data_row_label.name')}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="final-destination-location-name"
                    >
                        <Input
                            name="locationName"
                            value={formData.locationName}
                            onChange={value => handleFieldChange('locationName', value)}
                            className={cx(['global!ace-u-full-width', 'ace-c-input--small'])}
                        />
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.address')}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="final-destination-address"
                    >
                        {config.IS_FALLBACK_SYSTEM ? (
                            <SearchAutocomplete
                                name="locationSearchQuery"
                                placeholder={translateTab('input_placeholder.please_fill_in')}
                                value={selectedLocation?.formattedAddress || ''}
                                onChange={value => setSelectedLocation(() => ({formattedAddress: value}))}
                                onOptionSelect={handleLocationCandidateSelect}
                                shouldFilterChildren={false}
                                className={cx([
                                    'global!ace-u-full-width',
                                    'ace-c-autocomplete--small',
                                ])}
                                errors={errors?.location ? [errors.location] : []}
                            />
                        ) : (
                            <AutosuggestField
                                name="locationSearchQuery"
                                value={selectedLocation?.formattedAddress || ''}
                                onChange={handleLocationSearchQueryChange}
                                onOptionSelect={handleLocationCandidateSelect}
                                optionValueSelector={locationCandidate => locationCandidate.formattedAddress}
                                className={cx('global!ace-u-full-width')}
                                placeholder={translateTab('input_placeholder.please_fill_in')}
                                errors={errors?.location ? [errors.location] : []}
                                isSmall={true}
                            >
                                {locationSearchQuery.length >= config.MINIMUM_SEARCH_QUERY_LENGTH
                                    ? latestServiceAssignmentWithTowingDestination
                                    && latestServiceAssignmentWithTowingDestination?.finalDestinationLocationSearchQuery === lastLocationSearchQuery.current // eslint-disable-line max-len
                                        ? latestServiceAssignmentWithTowingDestination?.finalDestinationLocationCandidates // eslint-disable-line max-len
                                            .slice(0, config.ARCGIS_ADDRESS_GEOLOCATION_RESULTS_COUNT)
                                            .map((locationCandidate, index) => (
                                                <Option
                                                    key={index}
                                                    name={`relevant-location-candidate-${index}`}
                                                    value={locationCandidate}
                                                >
                                                    <Icon
                                                        icon={locationIcon}
                                                        className={cx('global!ace-u-margin--right-16')}
                                                    />
                                                    {locationCandidate.formattedAddress}
                                                </Option>
                                            ))
                                        : firstServiceAssignment?.finalDestinationLocationSearchQuery === lastLocationSearchQuery.current // eslint-disable-line max-len
                                            ? firstServiceAssignment?.finalDestinationLocationCandidates
                                                .slice(0, config.ARCGIS_ADDRESS_GEOLOCATION_RESULTS_COUNT)
                                                .map((locationCandidate, index) => (
                                                    <Option
                                                        key={index}
                                                        name={`relevant-location-candidate-${index}`}
                                                        value={locationCandidate}
                                                    >
                                                        <Icon
                                                            icon={locationIcon}
                                                            className={cx('global!ace-u-margin--right-16')}
                                                        />
                                                        {locationCandidate.formattedAddress}
                                                    </Option>
                                                )) : null
                                    : null}
                            </AutosuggestField>
                        )}
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.phone')}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="final-destination-phone"
                    >
                        <Input
                            name="phoneNo"
                            value={formData.phoneNo}
                            onChange={value => handleFieldChange('phoneNo', value)}
                            className={cx(['global!ace-u-full-width', 'ace-c-input--small'])}
                        />
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.working_hours')}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="final-destination-working-hours"
                    >
                        <div
                            className={cx([
                                'global!ace-u-flex',
                                'global!ace-u-flex--direction-column',
                            ])}
                        >
                            <div className={cx(['global!ace-u-flex', 'global!ace-u-flex--column-gap-24'])}>
                                <div className={cx('global!ace-u-flex--grow-1')}>
                                    <TimeField
                                        name="workingHoursFrom"
                                        value={formData.workingHoursFrom}
                                        onChange={value => handleFieldChange('workingHoursFrom', value)}
                                        inputClassName={cx('global!ace-u-width--full')}
                                        className={cx('ace-c-input-time--small')}
                                        errors={errors?.workingHoursFrom ? [errors.workingHoursFrom] : []}
                                    />
                                </div>
                                <div className={cx('global!ace-u-flex--grow-1')}>
                                    <TimeField
                                        name="workingHoursTo"
                                        value={formData.workingHoursTo}
                                        onChange={value => handleFieldChange('workingHoursTo', value)}
                                        inputClassName={cx('global!ace-u-width--full')}
                                        className={cx('ace-c-input-time--small')}
                                        errors={errors?.workingHoursTo ? [errors.workingHoursTo] : []}
                                    />
                                </div>
                            </div>
                            <Divider className={cx('global!ace-u-margin--8-0')} />
                            <div className={cx(['global!ace-u-flex', 'global!ace-u-flex--column-gap-24'])}>
                                <div className={cx('global!ace-u-flex--grow-1')}>
                                    <TimeField
                                        name="alternativeWorkingHoursFrom"
                                        value={formData.alternativeWorkingHoursFrom}
                                        onChange={value => handleFieldChange('alternativeWorkingHoursFrom', value)}
                                        inputClassName={cx('global!ace-u-width--full')}
                                        className={cx('ace-c-input-time--small')}
                                        errors={errors?.alternativeWorkingHoursFrom
                                            ? [errors.alternativeWorkingHoursFrom] : []}
                                    />
                                </div>
                                <div className={cx('global!ace-u-flex--grow-1')}>
                                    <TimeField
                                        name="alternativeWorkingHoursTo"
                                        value={formData.alternativeWorkingHoursTo}
                                        onChange={value => handleFieldChange('alternativeWorkingHoursTo', value)}
                                        inputClassName={cx('global!ace-u-width--full')}
                                        className={cx('ace-c-input-time--small')}
                                        errors={errors?.alternativeWorkingHoursTo
                                            ? [errors.alternativeWorkingHoursTo] : []}
                                    />
                                </div>
                            </div>
                        </div>
                    </DataRow>
                    <DataRow
                        label={translateTab('data_row_label.daily_fee', {
                            priceType: translate('global.pricing_type.net'),
                        })}
                        className={cx('ace-c-data-row--condensed')}
                        qaIdent="final-destination-daily-fee"
                    >
                        <InputCurrency
                            name="dailyFee"
                            value={formData.dailyFee}
                            onChange={value => handleFieldChange('dailyFee', value)}
                            min={0}
                            className={cx(['global!ace-u-full-width', 'ace-c-number-input--small'])}
                        />
                    </DataRow>
                    <div
                        className={cx([
                            'global!ace-u-width--full',
                            'global!ace-u-grid',
                        ])}
                    >
                        <div className={cx('global!ace-u-grid-column--span-5')} />
                        <ButtonPrimary
                            name="submitFinalDestinationButton"
                            type="submit"
                            className={cx([
                                'global!ace-u-margin--top-24',
                                'global!ace-u-width--full',
                                'global!ace-u-grid-column--span-7',
                            ], {
                                'ace-c-button-primary--is-dark-positive': !!isFinalDestinationSaved && !isFormTouched,
                            })}
                        >
                            {isFinalDestinationSaved && !isFormTouched
                                ? (
                                    <Fragment>
                                        {translateTab('button_label.accepted')}
                                        <Icon
                                            icon={checkmarkIcon}
                                            className={cx(
                                                'ace-c-icon--color-contrast',
                                                'global!ace-u-margin--left-16',
                                            )}
                                        />
                                    </Fragment>
                                ) : translateTab('button_label.accept')
                            }
                        </ButtonPrimary>
                    </div>
                </Form>
            </div>
        </Panel>
    );
};

SCBasicDataTowingDestinationInfoPanel.propTypes = {
    latestServiceAssignmentWithTowingDestination: PropTypes.object,
    serviceCase: PropTypes.object,
    serviceAssignments: PropTypes.object,
    initiateUpdateServiceCaseFinalDestination: PropTypes.func.isRequired,
    searchAssignmentFinalDestinationLocationGeolocation: PropTypes.func.isRequired,
};

SCBasicDataTowingDestinationInfoPanel.defaultProps = {
    latestServiceAssignmentWithTowingDestination: null,
    serviceCase: null,
    serviceAssignments: null,
};

const mapStateToProps = state => {
    return {
        serviceAssignments: state.serviceAssignments.serviceAssignments,
    };
};

const mapDispatchToProps = dispatch => ({
    initiateUpdateServiceCaseFinalDestination: payload => dispatch({
        type: serviceCaseActionTypes.INITIATE_SERVICE_CASE_FINAL_DESTINATION_UPDATE_FLOW,
        payload,
    }),
    searchAssignmentFinalDestinationLocationGeolocation: payload => dispatch({
        type: serviceAssignmentActionTypes.SEARCH_SERVICE_ASSIGNMENT_FINAL_DESTINATION_LOCATION_GEOLOCATION,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SCBasicDataTowingDestinationInfoPanel));
